import React from "react";
import { Link } from "react-router-dom";
import { fmtDate } from "@/lib/constants";

const TONE = {
  sold: "bg-emerald-100 border-emerald-700 text-emerald-900",
  pending: "bg-amber-100 border-amber-700 text-amber-900",
  bad: "bg-red-100 border-red-700 text-red-800",
  rejected: "bg-zinc-200 border-zinc-600 text-zinc-800",
};
const money = (n) => (n ? `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "—");

function Outcome({ r }) {
  return (
    <span className={`border px-1.5 py-0.5 text-[9px] uppercase tracking-widest whitespace-nowrap ${TONE[r.outcome]}`} data-testid={`results-outcome-${r.reference}`}>
      {r.status_label}
    </span>
  );
}

function Pager({ data, onPage }) {
  const pages = Math.max(1, Math.ceil((data.total || 0) / data.page_size));
  return (
    <div className="flex items-center gap-2 px-3 py-2 border-t-2 border-black text-xs" data-testid="results-pager">
      <span className="label-mono">{data.total} lead{data.total === 1 ? "" : "s"} · page {data.page_num} of {pages}</span>
      <button type="button" disabled={data.page_num <= 1} onClick={() => onPage(data.page_num - 1)} data-testid="results-prev"
              className="ml-auto border-2 border-black px-2 py-1 text-[10px] uppercase tracking-widest disabled:opacity-30">Prev</button>
      <button type="button" disabled={data.page_num >= pages} onClick={() => onPage(data.page_num + 1)} data-testid="results-next"
              className="border-2 border-black px-2 py-1 text-[10px] uppercase tracking-widest disabled:opacity-30">Next</button>
    </div>
  );
}

function Ref({ r, showAdmin }) {
  const text = <>#{r.reference}{r.is_test && <span className="ml-1 text-[9px] bg-amber-200 border border-black px-1">TEST</span>}</>;
  return showAdmin && r.lead_id
    ? <Link to={`/leads/${r.lead_id}`} className="underline font-mono font-bold">{text}</Link>
    : <span className="font-mono font-bold">{text}</span>;
}

export const ResultsTable = ({ data, showAdmin, onPage }) => {
  const rows = data?.leads || [];
  const th = "text-left py-2 px-3 text-[10px] uppercase tracking-widest";
  return (
    <section className="brut-card bg-white overflow-hidden" data-testid="results-table-card">
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-xs" data-testid="results-table">
          <thead className="bg-zinc-100 border-b-2 border-black">
            <tr>
              <th className={th}>Reference</th><th className={th}>When</th><th className={th}>State</th><th className={th}>Page</th>
              <th className={th}>Source</th><th className={th}>Firm</th><th className={th}>Status</th>
              {showAdmin && <><th className={th}>Media buyer</th><th className={th}>Sold for</th></>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.reference + r.created_at} className="border-b border-zinc-200 hover:bg-zinc-50" data-testid={`results-row-${r.reference}`}>
                <td className="py-2 px-3"><Ref r={r} showAdmin={showAdmin} /></td>
                <td className="py-2 px-3 text-[11px] text-zinc-600 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                <td className="py-2 px-3 font-mono">{r.state || "—"}</td>
                <td className="py-2 px-3 font-mono text-[11px]">{r.landing_page || "—"}</td>
                <td className="py-2 px-3 font-mono text-[11px]">{r.source}</td>
                <td className="py-2 px-3" data-testid={`results-firm-${r.reference}`}>{r.firm || "—"}</td>
                <td className="py-2 px-3"><Outcome r={r} /></td>
                {showAdmin && <><td className="py-2 px-3">{r.media_buyer || "—"}</td><td className="py-2 px-3 font-mono">{money(r.price)}</td></>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="md:hidden p-2 flex flex-col gap-2" data-testid="results-cards">
        {rows.map((r) => (
          <div key={r.reference + r.created_at} className="border border-black p-3" data-testid={`results-card-${r.reference}`}>
            <div className="flex items-center gap-2 mb-1"><Ref r={r} showAdmin={showAdmin} /><span className="ml-auto"><Outcome r={r} /></span></div>
            <div className="text-sm font-bold">{r.firm || "No firm yet"}</div>
            <div className="grid grid-cols-2 gap-x-3 text-[11px] font-mono text-zinc-700 mt-1">
              <div>State: {r.state || "—"}</div><div>Page: {r.landing_page || "—"}</div>
              <div>Source: {r.source}</div><div>{fmtDate(r.created_at)}</div>
              {showAdmin && <><div>Media buyer: {r.media_buyer || "—"}</div><div>Sold for: {money(r.price)}</div></>}
            </div>
          </div>
        ))}
      </div>
      {!rows.length && <div className="label-mono text-center py-10 text-zinc-500" data-testid="results-empty">No leads match these filters</div>}
      {data && <Pager data={data} onPage={onPage} />}
    </section>
  );
};
